import {Injectable} from '@angular/core';
import {
  AuditLogPage,
  AuditLogsService,
  PaymentTypeEnum,
  PurchaseTransaction,
  RefundStatusEnum,
  Response,
  TicketCompatibility,
  TicketReleaseType,
  TransactionsIdPaymentPutRequest,
  TransactionSortBy,
  TransactionsService as Api,
  TransactionType
} from '../../generated/sales-terminal-client';
import {Observable, tap} from 'rxjs';
import {SnackService} from '../snack/snack.service';
import {PageableSearch, SortableSearch} from '../../components/scroll-load-component/scroll-load.component';

export interface TransactionSearch extends SortableSearch {
  filters?: TransactionFilter;
}

export interface TransactionList {
  filter?: TransactionFilter;
  transactions?: Array<PurchaseTransaction>;
  total?: number;
}

// Combo of SelfServiceTransactionFilter and PurchaseTransactionFilter
export interface TransactionFilter extends SortableSearch {
  dateStart?: string;
  dateEnd?: string;
  transactionSalespointId?: number;
  paymentType?: PaymentTypeEnum;
  paymentMethod?: string;
  sellerUuid?: string;
  transactionId?: number;
  invoiceNumber?: number;
  eventId?: number;
  ticketId?: number;
  // SelfService unique filters
  email?: string;
  transactionType?: TransactionType;
  // Legacy unique filters
  customerUuid?: string;
  customerEmail?: string;
  sentFromBoEmail?: string;
  referenceNumber?: number;
  releaseType?: TicketReleaseType;
  buyerUuid?: string;
  searchEventOwnerTransactions?: boolean;
}

export interface PaymentReversalsSearch extends PageableSearch {
  dateStart?: string;
  dateEnd?: string;
  refundStatus?: RefundStatusEnum;
  eventId?: number;
}

@Injectable()
export class TransactionsService {
  constructor(private _api: Api,
              private snackService: SnackService,
              private auditLogsService: AuditLogsService,
  ) {}

  public searchLegacy(data: TransactionSearch): Observable<TransactionList> {
    return this._api.getTransactions(
      data.page,
      data.pageSize,
      data.sortBy,
      data.sortOrder,
      data.filters?.dateStart,
      data.filters?.dateEnd,
      data.filters?.transactionSalespointId || null,
      data.filters?.paymentType || null,
      data.filters?.sellerUuid || null,
      data.filters?.customerUuid || null,
      data.filters?.customerEmail || null,
      data.filters?.ticketId || null,
      data.filters?.referenceNumber?.toString() || null,
      data.filters?.transactionId || null,
      data.filters?.invoiceNumber || null,
      data.filters?.eventId || null,
      data.filters?.releaseType || null,
      data.filters?.sentFromBoEmail || null
    ).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public search(data: TransactionSearch): Observable<TransactionList> {
    return this._api.getTransactionList(
      data.page,
      data.pageSize,
      data.sortBy as TransactionSortBy,
      data.sortOrder,
      data.filters?.dateStart,
      data.filters?.dateEnd,
      data.filters?.transactionSalespointId || null,
      data.filters?.paymentType || null,
      data.filters?.paymentMethod || null,
      data.filters?.sellerUuid || null,
      data.filters?.ticketId || null,
      data.filters?.transactionId || null,
      data.filters?.invoiceNumber?.toString() || null,
      data.filters?.eventId || null,
      data.filters?.transactionType || null,
      data.filters?.email || null,
      data.filters?.buyerUuid || null,
      data.filters?.searchEventOwnerTransactions || false
    ).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public getTransactionLegacy(transactionId: number): Observable<PurchaseTransaction> {
    return this._api.getTransactionById(transactionId).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public getTransaction(transactionId: number): Observable<PurchaseTransaction> {
    return this._api.selfServiceTransactionIdGet(transactionId).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public updateTransactionPaymentType(transactionId: number, paymentType: TransactionsIdPaymentPutRequest): Observable<PurchaseTransaction> {
    return this._api.transactionsIdPaymentPut(transactionId, paymentType).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  public updateTransactionClientNotificationEmailLegacy(transactionId: number, clientNotificationEmail: string): Observable<PurchaseTransaction> {
    return this._api.transactionsIdClientnotificationemailPut(
      transactionId, { clientNotificationEmail: clientNotificationEmail }
    ).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  public updateTransactionClientNotificationEmail(transactionId: number, clientNotificationEmail: string): Observable<PurchaseTransaction> {
    return this._api.selfServiceTransactionIdClientNotificationEmailPut(
      transactionId, {clientNotificationEmail: clientNotificationEmail}
    ).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public updateTransactionInternalCommentLegacy(transactionId: number, internalComment: string): Observable<PurchaseTransaction> {
    return this._api.transactionsIdInternalcommentPut(
      transactionId, { internalComment: internalComment }
    ).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public updateTransactionInternalComment(transactionId: number, internalComment: string): Observable<PurchaseTransaction> {
    return this._api.selfServiceTransactionIdInternalCommentPut(
      transactionId, { internalComment: internalComment }
    ).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public transactionCompatibilityCheck(transactionId: number, tickets: number[]): Observable<TicketCompatibility[]> {
    return this._api.getTicketsTransactionCompatibility(transactionId, tickets).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public resendPurchaseEmail(transactionId: number, tickets: number[], newRecipientEmail?: string): Observable<Response> {
    return this._api.resendPurchaseEmail(transactionId, { tickets, newRecipientEmail }).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public getTicketsPdfCombined(transactionId: number, tickets: number[]): Observable<Blob> {
    return this._api.getTransactionTicketsPdfCombined(transactionId, { tickets }).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public getTransactionsPaymentMethods(paymentType: PaymentTypeEnum): Observable<string[]> {
    return this._api.getTransactionsPaymentMethods(paymentType).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public getAuditLogs(id: number, page: number, pageSize: number): Observable<AuditLogPage> {
    return this.auditLogsService.getTransactionAuditLogsByTransactionId(id, page, pageSize).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getYboAuditLogs(id: number, page: number, pageSize: number): Observable<AuditLogPage> {
    return this.auditLogsService.getTransactionYboAuditLogsByTransactionId(id, page, pageSize).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

}
